import React, { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { useMotionValue } from "framer-motion";
import { getAllDocsSnap } from "../../../utils/firebase/Firebase.utils";
import AnnouncementCard from "./AnnouncementCard";
import AnnouncementModal from "./AnnouncementModal";
import PhotoModal from "./PhotoModal";
import { IoIosArrowForward, IoIosArrowBack } from "react-icons/io";
import { useNavigate } from "react-router-dom";

function Announcements() {
  const containerRef = useRef(null);
  const [dragging, setDragging] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [mobileWidth, setMobileWidth] = useState(380);
  const [announcementArray, setAnnouncementArray] = useState([]);
  const [viewPhoto, setViewPhoto] = useState(false);
  const [selectedPhoto, setSelectedPhoto] = useState(null);
  const [selectedAnnouncement, setSelectedAnnouncement] = useState(null);
  const [viewAnnouncement, setViewAnnouncement] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const dragX = useMotionValue(0);

  useEffect(() => {
    const fetchAnnouncements = async () => {
      setIsLoading(true);
      await getAllDocsSnap("announcements", (data) => {
        const res = data;

        res.sort((a, b) => {
          return a.createdAt > b.createdAt ? -1 : 1;
        });

        const publishedData = res.filter((item) => item.status === "published");

        setAnnouncementArray(publishedData);
        setIsLoading(false);
      });
    };
    fetchAnnouncements();

    // Set mobile width based on screen size
    const handleResize = () => {
      setMobileWidth(window.innerWidth < 768 ? window.innerWidth - 40 : 380);
    };

    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const openPhotoModal = (e, photoUrl) => {
    e.stopPropagation();
    setSelectedPhoto(photoUrl);
    setViewPhoto(true);
  };

  const openAnnouncementModal = (announcement) => {
    if (!dragging) {
      setSelectedAnnouncement(announcement);
      setViewAnnouncement(true);
    }
  };

  const formatDate = (date) => {
    if (!date) return "";
    const options = {
      year: "numeric",
      month: "short",
      day: "numeric",
    };
    return new Date(date).toLocaleDateString("en-US", options);
  };

  const handlePrevious = () => {
    if (currentIndex > 0) {
      setCurrentIndex(currentIndex - 1);
    }
  };

  const handleNext = () => {
    if (currentIndex < announcementArray.length - 1) {
      setCurrentIndex(currentIndex + 1);
    }
  };

  const navigate = useNavigate();

  const navigateToAllAnnouncements = () => {
    navigate("/announcements");
  };

  return (
    <div className="bg-gray-200 rounded-xl shadow-sm px-4 mt-2">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-sm font-semibold text-[#363062]">Announcements</h2>
        <button
          className="text-xs text-[#363062] flex items-center"
          onClick={navigateToAllAnnouncements}
        >
          View All <IoIosArrowForward className="ml-1" />
        </button>
      </div>

      {isLoading ? (
        <div className="py-8">
          <div className="animate-pulse space-y-4">
            <div className="h-3 bg-gray-200 rounded w-3/4 mx-auto mb-2"></div>
            <div className="h-32 bg-gray-100 rounded"></div>
            <div className="h-2 bg-gray-200 rounded w-1/2 mx-auto"></div>
            <div className="flex justify-center space-x-2">
              <div className="h-1.5 w-1.5 bg-gray-200 rounded-full"></div>
              <div className="h-1.5 w-1.5 bg-gray-200 rounded-full"></div>
              <div className="h-1.5 w-1.5 bg-gray-200 rounded-full"></div>
            </div>
          </div>
        </div>
      ) : announcementArray.length > 0 ? (
        <div className="relative">
          {/* Carousel Container */}
          <div ref={containerRef} className="overflow-hidden rounded-xl">
            <motion.div
              className="flex"
              drag="x"
              dragConstraints={{
                left: -(mobileWidth * (announcementArray.length - 1)),
                right: 0,
              }}
              style={{ x: dragX }}
              dragElastic={0.1}
              onDragStart={() => setDragging(true)}
              onDragEnd={(e, { offset, velocity }) => {
                setDragging(false);

                if (Math.abs(offset.x) > mobileWidth / 4) {
                  const direction = offset.x < 0 ? 1 : -1;
                  const newIndex = Math.min(
                    Math.max(0, currentIndex + direction),
                    announcementArray.length - 1
                  );
                  setCurrentIndex(newIndex);
                }
              }}
              animate={{
                x: -currentIndex * mobileWidth,
              }}
              transition={{
                type: "spring",
                damping: 30,
                stiffness: 200,
              }}
            >
              {announcementArray.map((announcement, idx) => (
                <div
                  key={announcement.id}
                  className="min-w-[100%] md:min-w-[380px] px-2"
                  style={{ width: mobileWidth }}
                >
                  <AnnouncementCard
                    announcement={announcement}
                    onCardClick={openAnnouncementModal}
                    onPhotoClick={openPhotoModal}
                    formatDate={formatDate}
                  />
                </div>
              ))}
            </motion.div>
          </div>

          {/* Navigation Controls */}
          <div className="flex justify-between items-center mt-4">
            <button
              onClick={handlePrevious}
              disabled={currentIndex === 0}
              className={`w-8 h-8 rounded-full flex items-center justify-center shadow-sm ${
                currentIndex === 0
                  ? "bg-gray-100 text-gray-400"
                  : "bg-white text-[#363062] hover:bg-gray-50"
              }`}
            >
              <IoIosArrowBack size={16} />
            </button>

            {/* Dots Indicator */}
            <div className="flex justify-center space-x-1.5">
              {announcementArray.map((_, idx) => (
                <button
                  key={idx}
                  onClick={() => setCurrentIndex(idx)}
                  className={`h-1.5 w-1.5 rounded-full transition-all duration-300 ${
                    idx === currentIndex ? "bg-[#8FCD00] w-3" : "bg-gray-300"
                  }`}
                  aria-label={`Go to slide ${idx + 1}`}
                />
              ))}
            </div>

            <button
              onClick={handleNext}
              disabled={currentIndex === announcementArray.length - 1}
              className={`w-8 h-8 rounded-full flex items-center justify-center shadow-sm ${
                currentIndex === announcementArray.length - 1
                  ? "bg-gray-100 text-gray-400"
                  : "bg-white text-[#363062] hover:bg-gray-50"
              }`}
            >
              <IoIosArrowForward size={16} />
            </button>
          </div>
        </div>
      ) : (
        <div className="text-center py-8 text-gray-500 text-xs bg-gray-50 rounded-xl">
          <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-gray-100 flex items-center justify-center">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-6 w-6 text-gray-400"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
          </div>
          <p>No announcements available.</p>
          <p className="mt-1 text-[10px] text-gray-400">
            Check back later for updates
          </p>
        </div>
      )}

      {/* Photo Modal */}
      {viewPhoto && selectedPhoto && (
        <PhotoModal
          photoUrl={selectedPhoto}
          onClose={() => setViewPhoto(false)}
        />
      )}

      {/* Announcement Detail Modal */}
      {viewAnnouncement && selectedAnnouncement && (
        <AnnouncementModal
          announcement={selectedAnnouncement}
          onClose={() => setViewAnnouncement(false)}
          formatDate={formatDate}
        />
      )}
    </div>
  );
}

export default Announcements;
